/**
 * @GestionalePD User Profile page UI
 */
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import axios from 'axios';
import {
  Card,
  Stack,
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  List,
  ListItem,
  Avatar,
  Grid,
  FormGroup,
  FormControlLabel,
  TextField,
  CardHeader,
  Switch,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  CircularProgress,
  TableCell,
  CardContent,
  Tooltip,
  Alert,
  CardActionArea,
  Divider,
  useMediaQuery,
  Checkbox,
} from '@mui/material';
import {
  Autorenew as AutorenewIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  WarningAmber as WarningAmberIcon,
  Verified as VerifiedIcon,
  ContentCopy,
  Check as CheckIcon,
  ShoppingCart,
  Assignment as AssignmentIcon,
  Badge as BadgeIcon,
  GppGood as GppGoodIcon,
} from '@mui/icons-material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Navigate, useLocation } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import html2pdf from 'html2pdf.js';
// -----------------------------------------------------------------------------------------------------------------
import { usePopup } from '../../../context/PopupContext';
import RenewCardChoiceDialog from '../../../components/profile/RenewCardChoiceDialog';
import GdMasterEntitySelect from '../../../components/registration/GdMasterEntitySelect';
import { parseActiveRatesResponse, filterPaypalOnly } from '../../../utils/tesseramento/tesseramentoRates';
import { createFreeLinkedGdCard as requestFreeLinkedGdCard } from '../../../services/tesseramento/gdCardsService';
import { fetchGdEntities } from '../../../services/tesseramento/gdEntitiesService';
import {
  getActiveCampaign as fetchActiveCampaign,
  getActivePdGdCampaigns,
} from '../../../services/tesseramento/campaignsService';
import PaymentMethodOption from '../../../components/tesseramento/PaymentMethodOption';
import RatesTable from '../../../components/tesseramento/RatesTable';
import BankTransferInfo from '../../../components/tesseramento/BankTransferInfo';
import { isGdEligibleAge } from '../../../components/registration/registrationConfig';
import { CustomAutocomplete, CustomTextField, CustomDatePicker } from '../../../utils/StyledInputs';
import {
  DefaultOutlinedButton,
  DefaultContainedButton,
  CustomIconButton,
  RedOutlinedButton,
} from '../../../utils/Buttons';
import { validatePhoneNumber, getPrice } from '../../../utils/numberValidity';
import { PositionStatusLabel } from '../../../utils/PositionStatusLabel';
import { ResetPasswordDialog } from '../../../utils/ResetPasswordDialog';
import { ErrorUI, HttpErrorHandler } from '../../../utils/ErrorHandler';
import { EditPositionDialog } from '../../../utils/EditPositionDialog';
import { MultipleTabPanel, a11yProps } from '../../../utils/TabStyles';
import { NewPositionDialog } from '../../../utils/NewPositionDialog';
import { DecisionDialog } from '../../../utils/DecisionDialog';
import { LoadingUI } from '../../../utils/LayoutStyles';
import { PaypalCheckout } from '../../Checkout';
import { CommonLabel } from '../../../utils/Common';
import { DigitalCard, getCardDimensions } from '../../../utils/DigitalCard';
import GenderList from '../../../model/gender';
import API from '../../../api';

dayjs.locale('it');

/**
 * @returns User profile page UI
 */
export default function ProfilePage() {
  // general
  const location = useLocation();

  useEffect(() => {
    if (location.state && location.state.selectedTab !== undefined) {
      setValue(location.state.selectedTab);
    }
  }, [location.state]);

  const [isLoading, setIsLoading] = useState(true);
  const [isEditApisRunning, setIsEditApisRunning] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const is600Screen = useMediaQuery('(min-width: 600px)');
  const [edit, setEdit] = useState(false);
  const userLocalData = JSON.parse(localStorage.getItem('userData'));
  const [internalPosition, setInternalPosition] = useState([]);
  const [externalPosition, setExternalPosition] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState([]);
  const [newCardState, setNewCardState] = useState(false);
  const [hasValidPayedCard, setHasValidPayedCard] = useState(false);
  const [hasDownloadableCard, setHasDownloadableCard] = useState(false);
  const [currentProfile, setCurrentProfile] = useState('');

  const [errors, setErrors] = useState({
    name_alias: false,
    gender: false,
    home_country_id: false,
    home_street: false,
    home_zip_code: false,
    home_city_id: false,
    home_city_name: false,
    home_province_id: false,
    home_region_name: false,
    res_country_id: false,
    res_street: false,
    res_zip_code: false,
    res_city_id: false,
    res_city_name: false,
    res_province_id: false,
    res_region_name: false,
  });

  // popup handler
  const { showPopup, setPopUpText } = usePopup();

  // Tabs handler
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
    if (value === 1 && cards.length === 0) {
      setIsCardSectionLoading(true);
      getUserCards();
    }
  }, [value]);

  // newsletter subscription dialog
  const [openNewsletterDialog, setOpenNewsletterDialog] = useState(false);

  // open newsletter subscription dialog
  const onOpenNewsletterDialog = () => {
    setOpenNewsletterDialog(true);
  };

  // close newsletter subscription dialog
  const onCloseNewsletterDialog = () => {
    setOpenNewsletterDialog(false);
  };

  // change newsletter permission
  const changeNewsletterPermission = () => {
    API.patch(`users/${userInfo.id}/newsletters`, { allow_news: Number(!allowNews) })
      .then((response) => {
        onCloseNewsletterDialog();
        setAllowNews(!allowNews);

        const userData = JSON.parse(localStorage.getItem('userData')) || {};
        userData.allow_news = Number(!allowNews);
        localStorage.setItem('userData', JSON.stringify(userData));
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // ---------------------------- User details section ---------------------------------
  // Personal Data
  const [name, setName] = useState();
  const [surname, setSurname] = useState();
  const [gender, setGender] = useState();
  const [useAlias, setUseAlias] = useState(false);
  const [aliasName, setAliasName] = useState();
  const [fiscalCode, setFiscalCode] = useState();
  const [email, setEmail] = useState();
  const [birthDate, setBirthDate] = useState();
  const [birthPlace, setBirthPlace] = useState();
  const [phoneNumber, setPhoneNumber] = useState();
  const [callCode, setCallCode] = useState();
  const [userInfo, setUserInfo] = useState({});
  const [resEqualHome, setResEqualHome] = useState();
  const [userPosition, setUserPosition] = useState({});
  const [userEntity, setUserEntity] = useState([]);
  const [profileGdEntity, setProfileGdEntity] = useState(null);
  const [profileGdEntityList, setProfileGdEntityList] = useState([]);
  const [isBlacklistedEntity, setIsBlacklistedEntity] = useState(false);

  // api static data
  const [resRegionList, setResRegionList] = useState([]);
  const [resCountriesList, setResCountriesList] = useState([]);
  const [resProvincesList, setResProvincesList] = useState([]);
  const [resCitiesList, setResCitiesList] = useState([]);
  const [homeRegionList, setHomeRegionList] = useState([]);
  const [homeCountriesList, setHomeCountriesList] = useState([]);
  const [homeProvincesList, setHomeProvincesList] = useState([]);
  const [homeCitiesList, setHomeCitiesList] = useState([]);

  // Residence
  const [resCountry, setResCountry] = useState({});
  const [resRegion, setResRegion] = useState({});
  const [resProvince, setResProvince] = useState({});
  const [resCity, setResCity] = useState({});
  const [resStreet, setResStreet] = useState('');
  const [resZipCode, setResZipCode] = useState('');
  const [resCityName, setResCityName] = useState(null);
  const [resRegionName, setResRegionName] = useState(null);

  // Permenant address
  const [homeCountry, setHomeCountry] = useState({});
  const [homeRegion, setHomeRegion] = useState({});
  const [homeProvince, setHomeProvince] = useState({});
  const [homeCity, setHomeCity] = useState({});
  const [homeStreet, setHomeStreet] = useState('');
  const [homeZipCode, setHomeZipCode] = useState('');
  const [homeCityName, setHomeCityName] = useState(null);
  const [homeRegionName, setHomeRegionName] = useState(null);

  const [isGdAvailable, setIsGdAvailable] = useState(false);
  const [italy, setItaly] = useState({});

  useEffect(() => {
    if (birthDate) {
      const thirtyYearsAgo = dayjs().subtract(30, 'year');
      const hasLessThan30Years = dayjs(birthDate).isAfter(dayjs(thirtyYearsAgo));
      setIsGdAvailable(hasLessThan30Years);
    }
  }, [birthDate]);

  // Allow newsletter
  const [allowNews, setAllowNews] = useState(Boolean(userInfo.allow_news));

  // handler res equal home
  const onChangeHomePreferences = () => setResEqualHome(!resEqualHome);

  // Close Edit User Data
  const handleCloseEditButtonClick = () => {
    getUserDetails();
    setEdit(!edit);
  };

  // User E-mail contact verification
  const [openVerifyEmailDialog, setOpenVerifyEmailDialog] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState(false);
  const [isUserVerifyingEmail, setIsUserVerifyingEmail] = useState(false);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');

  // Open Verify Email Dialog
  const handleOpenVerifyEmailDialog = () => {
    setOpenVerifyEmailDialog(true);
  };

  // Close Verify Email Dialog
  const handleCloseVerifyEmailDialog = () => {
    setOpenVerifyEmailDialog(false);
    setIsUserVerifyingEmail(false);
  };

  // User phone number verification
  const [openVerifyPhoneDialog, setOpenVerifyPhoneDialog] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState(false);
  const [isUserVerifyingPhone, setIsUserVerifyingPhone] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');

  // Open Verify Phone Dialog
  const handleOpenVerifyPhoneDialog = () => {
    setOpenVerifyPhoneDialog(true);
    // sendOtpSms()
  };

  // Close Verify Phone Dialog
  const handleCloseVerifyPhoneDialog = () => {
    setOpenVerifyPhoneDialog(false);
    setIsUserVerifyingPhone(false);
  };

  // user reset password handler
  const [openResetPassword, setOpenResetPassword] = useState(false);

  // Open reset password model handler
  const handleOpenResetPassword = () => {
    setOpenResetPassword(true);
  };

  // Close reset password model handler
  const handleCloseResetPassword = () => {
    setOpenResetPassword(false);
  };

  useEffect(() => {
    if (!useAlias) {
      setAliasName(null);
    }
  }, [useAlias]);

  // load current user details
  const getUserDetails = () => {
    API.get(`/users/${userLocalData.id}`)
      .then((response) => {
        // General
        setUserInfo(response.data);

        // Personal Data
        setName(response.data.name);
        setSurname(response.data.surname);
        getDisplayableGender(response.data.gender);
        setAliasName(response.data.name_alias);
        if (response.data.name_alias && response.data.name_alias.length > 0) {
          setUseAlias(true);
        }
        setFiscalCode(response.data.fiscal_code);
        setBirthDate(response.data.birth_date);
        const placeOfBirth =
          response.data.place_of_birth !== null ? response.data.place_of_birth.name : response.data.place_of_birth;
        setBirthPlace(placeOfBirth);
        setEmail(response.data.email);
        setPhoneNumber(response.data.phone);
        setCallCode(response.data.call_code);
        setVerifiedEmail(!!response.data.verified_email);

        // Utils
        setResEqualHome(Boolean(response.data.res_equal_home));
        setHomeZipCode(response.data.home_zip_code);
        setHomeStreet(response.data.home_street);
        setResZipCode(response.data.res_zip_code);
        setResStreet(response.data.res_street);

        // entity data
        const userEntities = response.data.entities;
        const userMasterID = response.data.entity_id;
        const userMasterEntity = userEntities.find((entity) => entity.id === userMasterID);
        setUserEntity(userMasterEntity);
        // Valore entità GD: preferisci l'oggetto risolto dal BE (affidabile anche se l'entità non è
        // tra le user_entities o è fuori dal filtro residenza); fallback sulle user_entities.
        setProfileGdEntity(
          response.data.master_entity_gd ||
            userEntities.find((entity) => entity.id === response.data.entity_master_gd) ||
            null
        );
        setIsBlacklistedEntity(response.data.blacklisted_entity);

        // Newsletter preferences
        setAllowNews(Boolean(response.data.allow_news));

        setPositions(response.data.positions);

        // Residence
        setResCountry(response.data.res_country);
        setResRegion(response.data.res_region);
        setResProvince(response.data.res_province);
        setResCity(response.data.res_city);
        setResCityName(response.data.res_city_name);
        setResRegionName(response.data.res_region_name);

        // Permenant address
        setHomeCountry(response.data.home_country);
        setHomeRegion(response.data.home_region);
        setHomeProvince(response.data.home_province);
        setHomeCity(response.data.home_city);
        setHomeCityName(response.data.home_city_name);
        setHomeRegionName(response.data.home_region_name);

        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  // Opzioni entità master GD filtrate per residenza/domicilio (come in registrazione):
  // niente scelta libera su tutte le entità GD. Il valore già impostato viene comunque
  // risolto dalle user_entities (vedi sopra), quindi resta visibile anche se fuori dal filtro.
  useEffect(() => {
    const isResItaly = resCountry?.id && italy?.id ? resCountry.id === italy.id : !!resCity?.id;
    const city = isResItaly ? resCity : homeCity;
    const province = isResItaly ? resProvince : homeProvince;

    if (city?.id && province?.id) {
      fetchGdEntities({ cityId: city.id, provinceId: province.id })
        .then(({ entities }) => setProfileGdEntityList(entities))
        .catch(() => setProfileGdEntityList([]));
    } else {
      setProfileGdEntityList([]);
    }
  }, [resCountry, resCity, resProvince, homeCity, homeProvince, italy?.id]);

  // Risolve il valore corrente dell'entità GD dalle opzioni caricate (robusto anche se
  // l'entità non è tra le user_entities, es. impostata da un admin).
  useEffect(() => {
    if (userInfo?.entity_master_gd && profileGdEntityList.length > 0) {
      const match = profileGdEntityList.find((e) => e.id === userInfo.entity_master_gd);
      if (match) setProfileGdEntity(match);
    }
  }, [userInfo, profileGdEntityList]);

  const handleNewPosition = () => {
    getUserDetails();
    setUpdatedPositionsCheck(true);
    setUserRatesApiCalled(false);
  };

  const getUserCards = () => {
    API.get(`/memcards/user/${userLocalData.id}`)
      .then((response) => {
        setCards(response.data);

        const hasValidCard = response.data.some((card) => {
          return dayjs(card.campaign.end_date) > dayjs(new Date());
        });

        const hasLastPayedCard = response.data.some((card) => {
          return dayjs(card.campaign.end_date).isAfter(dayjs()) && card.payment_status.type === 'P';
        });

        const hasPayedCard = response.data.some((card) => {
          return card.payment_status.type === 'P' && card.prefix >= '2024';
        });

        setHasDownloadableCard(hasPayedCard);
        setHasValidPayedCard(hasLastPayedCard);
        setNewCardState(hasValidCard);
        setIsCardSectionLoading(false);
      })
      .catch((error) => {
        setIsCardSectionLoading(false);
        setIsLoading(false);
        HttpErrorHandler(error, setErrorMessage);
      });
  };

  // Get address form static data like contries, regions, provines and cities
  const getAddressApi = () => {
    if (resCountriesList.length === 0 || homeCountriesList.length === 0) {
      axios
        .all([API.get('/countries')])
        .then(
          axios.spread((countriesResponse) => {
            setResCountriesList(countriesResponse.data);
            setHomeCountriesList(countriesResponse.data);

            const italyData = countriesResponse.data.find((element) => element.name === 'Italia');
            setItaly(italyData);

            setIsEditApisRunning(false);
            setEdit(true);
          })
        )
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    } else {
      setIsEditApisRunning(false);
      setEdit(true);
    }
  };

  useEffect(() => {
    if (edit && resCountry && resCountry?.name !== italy?.name) {
      setResProvince('');
      setResRegion('');
      setResCity('');
    }

    if (resCountry?.name !== italy?.name) {
      setResRegionList([]);
      setResProvincesList([]);
      setResCitiesList([]);
    }
  }, [resCountry]);

  useEffect(() => {
    if (edit && homeCountry && homeCountry?.name !== italy?.name) {
      setHomeProvince('');
      setHomeRegion('');
      setHomeCity('');
    }

    if (homeCountry?.name !== italy?.name) {
      setHomeRegionList([]);
      setHomeProvincesList([]);
      setHomeCitiesList([]);
    }
  }, [homeCountry]);

  // Get resRegionList
  useEffect(() => {
    if (edit) {
      if (resCountry !== null && resCountry !== undefined && resCountry !== '') {
        if (typeof resCountry === 'object' && Object.keys(resCountry).length > 0 && resCountry?.name === italy.name) {
          API.get(`/countries/regions`, {
            params: {
              id: resCountry.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setResRegionList(response.data);

              if (resEqualHome) {
                setHomeRegionList(response.data);
              }
            } else {
              setResRegionList([]);
              setResProvincesList([]);
              setResCitiesList([]);
            }
          });
        }
      }
    }
  }, [resCountry, edit]);

  // Get resProvincesList
  useEffect(() => {
    if (edit) {
      if (resRegion !== null && resRegion !== undefined && resRegion !== '') {
        if (typeof resRegion === 'object' && Object.keys(resRegion).length > 0 && resCountry?.name === italy.name) {
          API.get(`/regions/provinces`, {
            params: {
              region_name: resRegion.region_name,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setResProvincesList(response.data);

              if (resEqualHome) {
                setHomeProvincesList(response.data);
              }
            } else {
              setResProvincesList([]);
              setResCitiesList([]);
            }
          });
        }
      }
    }
  }, [resRegion, edit]);

  // Get resCitiesList
  useEffect(() => {
    if (edit) {
      if (resProvince !== null && resProvince !== undefined && resProvince !== '') {
        if (typeof resProvince === 'object' && Object.keys(resProvince).length > 0) {
          API.get(`/provinces/cities`, {
            params: {
              id: resProvince.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setResCitiesList(response.data);
              if (resEqualHome) {
                setHomeCitiesList(response.data);
              }
            } else {
              setResCitiesList([]);
            }
          });
        }
      }
    }
  }, [resProvince, edit]);

  // Get homeRegionList
  useEffect(() => {
    if (edit && !resEqualHome) {
      if (homeCountry !== null && homeCountry !== undefined && homeCountry !== '') {
        if (
          typeof homeCountry === 'object' &&
          Object.keys(homeCountry).length > 0 &&
          homeCountry?.name === italy.name
        ) {
          API.get(`/countries/regions`, {
            params: {
              id: homeCountry.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setHomeRegionList(response.data);
            } else {
              setHomeRegionList([]);
              setHomeProvincesList([]);
              setHomeCitiesList([]);
            }
          });
        }
      }
    }
  }, [homeCountry, edit]);

  // Get homeProvincesList
  useEffect(() => {
    if (edit && !resEqualHome) {
      if (homeRegion !== null && homeRegion !== undefined && homeRegion !== '') {
        if (typeof homeRegion === 'object' && Object.keys(homeRegion).length > 0 && homeCountry?.name === italy.name) {
          API.get(`/regions/provinces`, {
            params: {
              region_name: homeRegion.region_name,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setHomeProvincesList(response.data);
            } else {
              setHomeProvincesList([]);
              setHomeCitiesList([]);
            }
          });
        }
      }
    }
  }, [homeRegion, edit]);

  // Get homeCitiesList
  useEffect(() => {
    if (edit && !resEqualHome) {
      if (homeProvince !== null && homeProvince !== undefined && homeProvince !== '') {
        if (typeof homeProvince === 'object' && Object.keys(homeProvince).length > 0) {
          API.get(`/provinces/cities`, {
            params: {
              id: homeProvince.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setHomeCitiesList(response.data);
            } else {
              setHomeCitiesList([]);
            }
          });
        }
      }
    }
  }, [homeProvince, edit]);

  // Send OTP Eail Verification Code
  const sendOtpEmail = () => {
    setIsUserVerifyingEmail(true);
    API.patch(`/otp/send_mail`, { email: `${email}` })
      .then((response) => {
        if (response.status === 200) {
          setPopUpText('Email inviata con successo');
          showPopup(true);
        }
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // verify user e-mail
  const onVerifyUserEmail = () => {
    const otpCheckData = { otp: emailVerificationCode, email };

    API.post(`/checkemailotp`, otpCheckData)
      .then((response) => {
        setPopUpText('Email verificata con successo');

        showPopup(true);
        setVerifiedEmail(true);
        getUserDetails();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // Send OTP Sms Verification Code
  const sendOtpSms = () => {
    setIsUserVerifyingPhone(true);
    const countryCode = callCode.slice(1, callCode.length);

    API.patch(`/otp/send_sms?phone=${phoneNumber}&country_code=${countryCode}`)
      .then((response) => {
        setPopUpText('SMS inviato con successo');
        showPopup(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const getGenderDBName = (name) => {
    let gender;

    if (name === 'uomo') {
      gender = 'male';
    } else if (name === 'donna') {
      gender = 'female';
    } else if (name === 'altro') {
      gender = 'other';
    } else {
      gender = null;
    }

    return gender;
  };

  // Get Gender Name
  const getDisplayableGender = (gender) => {
    let genderName;

    if (gender === 'male') {
      genderName = 'Uomo';
    } else if (gender === 'female') {
      genderName = 'Donna';
    } else if (gender === 'other') {
      genderName = 'Altro';
    } else {
      genderName = '';
    }

    return setGender({ gender: genderName });
  };

  // verify user phone
  const onVerifyUserPhone = () => {
    API.post(`/checksmsotp?otp=${phoneVerificationCode}&phone=${callCode}${phoneNumber}`)
      .then((response) => {
        setPopUpText('Numero verificato con successo');
        showPopup(true);
        setVerifiedPhone(true);
        getUserDetails();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // Update user data request
  const onUpdateUserData = (e) => {
    e.preventDefault();

    if (edit) {
      // user residence data
      const resCityID =
        resCity === null ? null : resCity.id ? resCity.id : resCity === '' ? userInfo.res_city_id : null;

      const _resCityName = typeof resCityName === 'string' ? resCityName : null;

      const resProvinceID =
        resProvince === null
          ? null
          : resProvince.id
          ? resProvince.id
          : resProvince === ''
          ? userInfo.res_province_id
          : null;

      const _resRegionName = typeof resRegionName === 'string' ? resRegionName : null;

      const resCountryID =
        resCountry === null ? null : resCountry.id ? resCountry.id : resCountry === '' ? userInfo.res_country_id : null;

      // Permenant residence data
      const homeCityID = resEqualHome
        ? resCityID
        : !resEqualHome && typeof homeCity.id === 'string' && homeCity.id !== ''
        ? homeCity.id
        : !resEqualHome && typeof homeCity.id === 'string' && homeCity.id === ''
        ? null
        : null;

      const _homeCityName = typeof homeCityName === 'string' ? homeCityName : null;

      const homeProvinceID = resEqualHome
        ? resProvinceID
        : !resEqualHome && homeProvince && typeof homeProvince.id === 'string' && homeCity.id !== ''
        ? homeProvince.id
        : !resEqualHome && homeProvince && typeof homeProvince.id === 'string' && homeProvince.id === ''
        ? null
        : null;

      const _homeRegionName = typeof homeRegionName === 'string' ? homeRegionName : null;

      const homeCountryID = resEqualHome
        ? resCountryID
        : !resEqualHome && homeCountry && typeof homeCountry.id === 'string' && homeCountry.id !== ''
        ? homeCountry.id
        : !resEqualHome && homeCountry && typeof homeCountry.id === 'string' && homeCountry.id === ''
        ? null
        : null;

      const homeStreetValue = resEqualHome ? resStreet : homeStreet;
      const homeZipCodeValue = resEqualHome ? resZipCode : homeZipCode;

      const genderName = getGenderDBName(gender && gender.gender ? gender.gender.toLowerCase() : '');

      // user data object to be modified
      const updatedData = {
        name,
        surname,
        fiscal_code: userInfo.fiscal_code,
        email,
        name_alias: aliasName,
        gender: genderName,
        birth_date: dayjs(birthDate).format('YYYY/MM/DD'),
        place_of_birth: userInfo.place_of_birth.id,
        phone: phoneNumber,
        call_code: callCode,
        res_zip_code: resZipCode,
        res_street: resStreet,
        res_country_id: resCountryID,
        res_equal_home: Number(resEqualHome),
        home_zip_code: homeZipCodeValue,
        home_street: homeStreetValue,
        home_country_id: homeCountryID,
        user_online: userInfo.user_online,
        entity_id: userInfo.entity_id,
        // Immutabile una volta impostata: se già presente si mantiene il valore persistito
        // (il campo è bloccato), altrimenti si usa l'entità appena scelta.
        entity_master_gd: userInfo?.entity_master_gd || (profileGdEntity ? profileGdEntity.id : null),
        verified: userInfo.verified,
        card_holder: userInfo.card_holder,
        allow_news: Number(allowNews),
        user_roles: userInfo.roles,
      };

      let itemsLeft = false;
      const newErrors = { ...errors };

      Object.keys(updatedData).forEach((key) => {
        const value = updatedData[key];

        if (Object.prototype.hasOwnProperty.call(updatedData, key)) {
          if (
            value === undefined ||
            value === null ||
            value === 'Invalid Date' ||
            (typeof value === 'string' && value.length === 0)
          ) {
            if (useAlias || !['name_alias'].includes(key)) {
              itemsLeft = true;
              if (Object.prototype.hasOwnProperty.call(newErrors, key)) {
                newErrors[key] = true;
              }
              showPopup('warning', 'Inserisci i dati mancanti');
            }
          } else {
            newErrors[key] = false;
          }
        }
      });

      updatedData.res_city_id = typeof _resCityName !== 'string' ? resCityID : null;
      updatedData.res_city_name = _resCityName;
      updatedData.res_province_id = typeof _resRegionName !== 'string' ? resProvinceID : null;
      updatedData.res_region_name = _resRegionName;
      updatedData.home_city_id = resEqualHome ? updatedData.res_city_id : homeCityID;
      updatedData.home_province_id = resEqualHome ? updatedData.res_province_id : homeProvinceID;
      updatedData.home_city_name = resEqualHome ? _resCityName : _homeCityName;
      updatedData.home_region_name = resEqualHome ? _resRegionName : _homeRegionName;

      setErrors(newErrors);

      if (!itemsLeft) {
        API.put(`/users/${userInfo.id}`, updatedData)
          .then((response) => {
            const existingUserData = JSON.parse(localStorage.getItem('userData')) || {};

            const newUserData = {
              birth_date: updatedData.birth_date,
              allow_news: updatedData.allow_news,
              id: userInfo.id,
              card_holder: updatedData.card_holder,
              name: updatedData.name,
              roles: userInfo.roles,
              surname: updatedData.surname,
              fiscal_code: updatedData.fiscal_code,
              verified: updatedData.verified,
            };

            const updatedUserData = {
              ...existingUserData,
              ...newUserData,
            };

            localStorage.setItem('userData', JSON.stringify(updatedUserData));
            setPopUpText('Informazioni salvate');
            showPopup(true);
          })
          .catch((error) => {
            HttpErrorHandler(error, setPopUpText);
            showPopup(false);
          });
        setEdit(false);
      }
    } else {
      setIsEditApisRunning(true);
      getAddressApi();
    }
  };

  // ---------------------------- User Position section ----------------------------------
  // User position internal tab handler
  const [positionTabValue, setPositionTabValue] = useState(0);
  const handleChangePositionTab = (event, newValue) => {
    setPositionTabValue(newValue);

    if (newValue === 0) {
      setSelectedPosition(externalPosition);
    } else {
      setSelectedPosition(internalPosition);
    }
  };

  const [openNewPosition, setOpenNewPosition] = useState(false);

  const handleOpenNewPosition = () => {
    setOpenNewPosition(true);
  };

  const handleCloseNewPosition = () => {
    setOpenNewPosition(false);
  };

  // user position role handler
  const [positions, setPositions] = useState([]);
  const [updatedPositionsCheck, setUpdatedPositionsCheck] = useState(false);
  const [openEditPosition, setOpenEditPosition] = useState(false);
  const [positionToEdit, setPositionToEdit] = useState({});

  // Open Edit Position Modal
  const handleOpenEditPosition = (position) => {
    setOpenEditPosition(true);
    setPositionToEdit(position);
  };

  // Close Edit Position Modal
  const handleCloseEditPosition = () => {
    setOpenEditPosition(false);
  };

  const [openDeletePosition, setOpenDeletePosition] = useState(false);

  const openDeletePositionDialog = (position) => {
    setPositionToEdit(position);
    setOpenDeletePosition(true);
  };

  const closeDeletePositionDialog = () => {
    setPositionToEdit('');
    setOpenDeletePosition(false);
  };

  const deletePosition = (event) => {
    event.preventDefault();

    API.delete(`/charges/${positionToEdit.id}`)
      .then(() => {
        getUserDetails();
        closeDeletePositionDialog();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // ---------------------------- User Membership card section -------------------------
  const [userRates, setUserRates] = useState([]);
  const [activeCampaign, setActiveCampaign] = useState({});
  const [activeCampaignApiCalled, setActiveCampaignApiCalled] = useState(false);
  const [userRatesApiCalled, setUserRatesApiCalled] = useState(false);
  const [totalPrice, setTotalPrice] = useState([]);
  const [disabledStates, setDisabledStates] = useState(userRates.map(() => false));
  const [cards, setCards] = useState([]);
  const [isCardSectionLoading, setIsCardSectionLoading] = useState(false);

  // paypal handler
  const [orderItems, setOrderItems] = useState([]);
  const [isPaying, setIsPaying] = useState(false);
  const [paypalSuccess, setPaypalSuccess] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState({ select: false, method: null, type: null });
  const [paymentStatus, setPaymentStatus] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentTypesApiCalled, setPaymentTypesApiCalled] = useState(false);
  const [subscriptionApiCalled, setSubscriptionApiCalled] = useState(false);
  const [subscriptionApiError, setSubscriptionApiError] = useState('');

  // Membership card handler
  const [openNewCard, setOpenNewCard] = useState(false);
  const [onlineMembershipCard, setOnlineMembershipCard] = useState(true);
  const [membershipCard, setMembershipCard] = useState({
    id: null,
    prefix: null,
    card_number: null,
    campaign_id: null,
    campaign_name: null,
    entity_name: null,
    amount: null,
    currency: null,
    creation_date: null,
    payment_status: null,
    payment_type: null,
  });

  // --- Rinnovo con scelta tessere (R20) ---
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewOptions, setRenewOptions] = useState({ gdOnly: false, gdPlusPd: false });
  const [renewLoading, setRenewLoading] = useState(false);
  const [renewMode, setRenewMode] = useState('PD'); // 'PD' | 'GD' | 'PD_GD'
  const [gdEntitySel, setGdEntitySel] = useState(null); // entity_master_gd scelta
  // Tessera PD dell'anno corrente già posseduta: se valorizzata, la GD è gratuita e collegata a essa.
  const [gdParentCardId, setGdParentCardId] = useState(null);
  const [gdEntityList, setGdEntityList] = useState([]);
  const [needGdEntity, setNeedGdEntity] = useState(false);
  // Pagamento di una tessera GD (nuovo tesseramento o GD già creata, es. da /join-gd e pagata
  // dopo): ammesso solo PayPal. Ci basiamo sul tipo della tessera in pagamento, non su renewMode
  // (che potrebbe essere stantio e filtrare erroneamente un pagamento PD).
  const isGdPayment = membershipCard?.campaign_type === 'GD';
  const [campaignGd, setCampaignGd] = useState({});
  const [gdBundleRates, setGdBundleRates] = useState([]);

  /* ------------------------------- New Card ------------------------------ */
  const [openCreateCardDialog, setOpenCreateCardDialog] = useState(false);

  useEffect(() => {
    const shouldCallSubscriptionApi = isPaying ? paymentTypesApiCalled : userRatesApiCalled && paymentTypesApiCalled;

    if (shouldCallSubscriptionApi) {
      setSubscriptionApiCalled(true);
    }
  }, [isPaying, userRatesApiCalled, paymentTypesApiCalled]);

  // Open New Card Dialog
  const handleOpenCreateCardDialog = () => {
    setOpenCreateCardDialog(true);
  };

  // Close New Card Dialog
  const handleCloseCreateCardDialog = () => {
    setOpenCreateCardDialog(false);
  };

  // Open New Card Modal
  const handleOpenNewCard = (getActiveRates) => {
    setOpenNewCard(true);
    if (getActiveRates) {
      // force: ricarica sempre le tariffe. Senza, il guard su userRates.length salterebbe il
      // refetch e mostrerebbe tariffe stantie (es. quelle GD dopo aver chiuso e riaperto in PD).
      getMembershipCardRates(userPosition, { force: true });
    }
    getActiveCampaign();
    getPaymentStatus();
    getPaymentTypes();
  };

  // --- Rinnovo con scelta tessere (R20) ---
  // Apre il pop-up di scelta se ci sono opzioni GD disponibili; altrimenti procede
  // direttamente col rinnovo PD (nessuna regressione per chi non è eleggibile ai GD).
  const openRenewDialog = () => {
    setRenewLoading(true);
    setRenewDialogOpen(true);
    setGdEntitySel(null);

    const age = birthDate ? dayjs().diff(dayjs(birthDate), 'year') : null;
    const gdAgeOk = isGdEligibleAge(age);
    const pdAgeOk = age === null || age >= 16; // il tesseramento PD richiede almeno 16 anni
    const profileHasGdEntity = !!userInfo.entity_master_gd;
    setNeedGdEntity(!profileHasGdEntity);

    if (!profileHasGdEntity && gdAgeOk) {
      fetchGdEntities()
        .then(({ entities }) => setGdEntityList(entities))
        .catch(() => setGdEntityList([]));
    }

    getActivePdGdCampaigns().then(({ pd, gd }) => {
      setCampaignGd(gd || {});

      // Multi-campagna: si può creare una tessera per ogni campagna ATTIVA non ancora posseduta.
      // Escludiamo le campagne per cui l'utente ha già una tessera (il BE blocca comunque i doppioni).
      const pdHeld = pd && cards.some((c) => c.campaign_id === pd.id);
      const gdHeld = gd && cards.some((c) => c.campaign_id === gd.id);
      // Se l'utente ha già la PD dell'anno corrente, la GD sarà gratuita e collegata a quella tessera.
      // NB: /memcards/user/{id} restituisce i modelli raw ⇒ l'id tessera è `id` (non `card_id`).
      const currentPdCard = pd ? cards.find((c) => c.campaign_id === pd.id) : null;
      setGdParentCardId(currentPdCard?.id || null);

      const gdOnly = !!gd && gdAgeOk && !gdHeld;
      // PD e bundle PD+GD richiedono ≥16 (il PD è incluso); il GD-only resta 14–29.
      const gdPlusPd = !!pd && !!gd && pdAgeOk && gdAgeOk && !pdHeld && !gdHeld;
      setRenewOptions({ pd: !!pd && pdAgeOk && !pdHeld, gdOnly, gdPlusPd });
      setRenewLoading(false);
    });
  };

  // Crea la tessera GD gratuita collegata alla PD dell'anno corrente (l'utente ha già la PD).
  // Importo 0 forzato lato server per la presenza di parent_id; nessun pagamento richiesto.
  const createFreeLinkedGdCard = async (parentCardId, gdEntityId) => {
    try {
      const res = await requestFreeLinkedGdCard({
        userId: userInfo.id,
        parentCardId,
        gdEntityId,
        birthDate,
        digitalFormat: onlineMembershipCard,
      });
      if (!res.ok) {
        showPopup('warning', 'Campagna GD non disponibile');
        return;
      }
      getUserCards();
      setPopUpText('Informazioni salvate');
      showPopup(true);
    } catch (e) {
      HttpErrorHandler(e, setPopUpText);
      showPopup(false);
    }
  };

  const handleRenewChoose = async (choice) => {
    // Reset dello stato transitorio: un nuovo tentativo non deve mostrare errori/tariffe precedenti.
    setSubscriptionApiError('');
    setUserRatesApiCalled(false);
    // PD: nessuna entità GD richiesta, flusso di creazione tessera PD classico.
    if (choice === 'PD') {
      setRenewMode('PD');
      setRenewDialogOpen(false);
      handleOpenNewCard(true);
      return;
    }

    const gdEntityId = userInfo.entity_master_gd || gdEntitySel?.id;
    if (!gdEntityId) {
      showPopup('warning', "Selezionare un'entità master GD.");
      return;
    }

    // Persist entity_master_gd sul profilo se scelta ora (contract §1B).
    if (!userInfo.entity_master_gd && gdEntitySel?.id) {
      try {
        await API.put(`/users/${userInfo.id}`, {
          entity_id: userEntity.id,
          gd_check: true,
          entity_master_gd: gdEntitySel.id,
        });
        setUserInfo((prev) => ({ ...prev, entity_master_gd: gdEntitySel.id }));
      } catch (e) {
        HttpErrorHandler(e, setPopUpText);
        showPopup(false);
        return;
      }
    }

    setRenewMode(choice);
    setRenewDialogOpen(false);
    setUserRates([]);
    getPaymentStatus();
    getPaymentTypes();

    if (choice === 'GD') {
      // Utente con PD dell'anno corrente ⇒ la tessera GD è gratuita e collegata alla PD (nessun
      // pagamento). Solo chi NON ha la PD dell'anno paga la quota GD.
      if (gdParentCardId) {
        await createFreeLinkedGdCard(gdParentCardId, gdEntityId);
        return;
      }
      getActiveCampaign('GD');
      getMembershipCardRates(userPosition, { campaignType: 'GD', entityId: gdEntityId, force: true });
      setOpenNewCard(true);
    } else {
      // PD_GD: prepara le rate GD (importo 0) per la tessera figlia, poi flusso PD.
      API.get(
        `active_rates/?position_id=&entity_id=${gdEntityId}&campaign_type=GD&user_birth_date=${dayjs(birthDate).format(
          'YYYY/MM/DD'
        )}`
      )
        .then((r) => setGdBundleRates((r.data.entity_rate || []).map((o) => ({ id: o.rate.id, price: 0 }))))
        .catch(() => setGdBundleRates([]));
      getActiveCampaign('PD');
      getMembershipCardRates(userPosition, { campaignType: 'PD', entityId: userEntity.id, force: true });
      setOpenNewCard(true);
    }
  };

  useEffect(() => {
    if (paymentMethod.select && paymentMethod.type === 'BB') {
      if (ibanCode.length === 0) {
        setIsBankDataLoading(true);
      }
      getBankData();
    }
  }, [paymentMethod]);

  // Tessera GD: unico metodo ammesso è PayPal. Evitiamo lo step con l'unica card di selezione
  // (che appariva "vuoto"): selezioniamo automaticamente PayPal e mostriamo direttamente il
  // checkout (importo + pulsanti di pagamento).
  useEffect(() => {
    if (isPaying && isGdPayment && !paymentMethod.select) {
      const paypalType = (Array.isArray(paymentTypes) ? paymentTypes : []).find((t) => t.type === 'PCC');
      if (paypalType) {
        membershipCard.payment_type = paypalType.id;
        setPaymentMethod({ select: true, method: paypalType.name, type: 'PCC' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaying, isGdPayment, paymentMethod.select, paymentTypes]);

  // Close New Card Modal
  const handleCloseNewCard = () => {
    setOpenNewCard(false);
    setIsPaying(false);
    setPaypalSuccess(false);
    setPaymentMethod({ select: false, method: null, type: null });
    setSubscriptionApiError('');
    // Azzera le tariffe: alla riapertura (anche con un tipo diverso, es. GD→PD) si riparte pulito
    // e si evita di mostrare le tariffe del tesseramento precedente.
    setUserRates([]);
    setUserRatesApiCalled(false);
  };

  // Rates Inputs Disabled
  const handleDisabledInput = (index) => {
    const newDisabledStates = [...disabledStates];
    newDisabledStates[index] = !newDisabledStates[index];
    setDisabledStates(newDisabledStates);
  };

  const [otherValue, setOtherValue] = useState({});

  const isSaveEnabled = !Number.isNaN(totalPrice) && totalPrice > 0;

  // Rates Changes handler
  const handleRateChange = (index, newValue) => {
    setUserRates((prevRates) => {
      const newRates = [...prevRates];
      newRates[index].price = Number(newValue).toFixed(2).toString();
      newRates[index].price_discounted = Number(newValue).toFixed(2).toString();
      return newRates;
    });
  };

  // get payment status data
  const getPaymentStatus = () => {
    if (Object.keys(paymentStatus).length === 0) {
      API.get('/paystatus')
        .then((response) => {
          setPaymentStatus(response.data || []);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  // get payment type data
  const getPaymentTypes = () => {
    if (Object.keys(paymentTypes).length === 0) {
      API.get('/paytypes?for_user=1')
        .then((response) => {
          setPaymentTypes(response.data.payment_types || []);
          setPaymentTypesApiCalled(true);
        })
        .catch((error) => {
          HttpErrorHandler(error, setSubscriptionApiError);
        });
    }
  };

  // Get Card Rates
  const getMembershipCardRates = (positionObj, opts = {}) => {
    const { campaignType = 'PD', entityId = userEntity.id, force = false } = opts;
    const apiPosition = Object.keys(positionObj).length > 0 ? positionObj.id : '';
    const typeParam = campaignType && campaignType !== 'PD' ? `&campaign_type=${campaignType}` : '';

    if (force || userRates.length === 0 || updatedPositionsCheck) {
      API.get(
        `active_rates/?position_id=${apiPosition}&entity_id=${entityId}${typeParam}&user_birth_date=${dayjs(
          birthDate
        ).format('YYYY/MM/DD')}`
      )
        .then((response) => {
          const activeRates = parseActiveRatesResponse(response.data);

          setUserRates(activeRates);

          setUserRatesApiCalled(true);
          setUpdatedPositionsCheck(false);
        })
        .catch((error) => {
          HttpErrorHandler(error, setSubscriptionApiError);
        });
    }
  };

  const getActiveCampaign = (type = 'PD') => {
    fetchActiveCampaign(type)
      .then((data) => {
        setActiveCampaign(data);
        setActiveCampaignApiCalled(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setSubscriptionApiError);
      });
  };

  // Get Rates Total Value
  useEffect(() => {
    let ratesSum;

    if (isGdAvailable) {
      ratesSum = userRates.reduce((sum, rate) => sum + Number(rate.price_discounted), 0);
    } else {
      ratesSum = userRates.reduce((sum, rate) => sum + Number(rate.price), 0);
    }
    setTotalPrice(ratesSum);
  }, [userRates]);

  // Creating new membership card
  const onCreateNewMembershipCard = (e) => {
    e.preventDefault();

    // GD: tariffa singola (mai sconto giovani PD). R2/G4.
    const singleRate = renewMode === 'GD';
    let _amount = 0;
    const items = [];
    const _paypalOrder = [];

    const newCardRates = {
      user_id: userInfo.id,
      campaign_id: userRates[0].campaign_id,
      rates: [],
      // GD-only: paga sull'entità master GD (coerente con le tariffe), non sull'entità PD.
      payment_entity_id: singleRate ? userInfo.entity_master_gd || gdEntitySel?.id : userEntity.id,
      payment_online: 1,
      digital_format: Number(onlineMembershipCard),
    };

    paymentStatus.forEach((status) => {
      if (status.type === 'W') {
        newCardRates.payment_status = status.id;
      }
    });

    userRates.forEach((element) => {
      if (element.price !== 0 && element.price !== 0.0 && element.price !== '0.00') {
        newCardRates.rates.push({
          id: element.id,
          price: !singleRate && isGdAvailable ? Number(element.price_discounted) : Number(element.price),
        });

        items.push({
          reference_id: element.id,
          amount: {
            currency_code: element.currency,
            value: !singleRate && isGdAvailable ? element.price_discounted : element.price,
          },
          description: element.name,
        });

        if (!singleRate && isGdAvailable) {
          _amount += parseFloat(element.price_discounted);
        } else {
          _amount += parseFloat(element.price);
        }
      }
    });

    if (items.length !== 0) {
      API.post('/memcards', newCardRates)
        .then((response) => {
          setMembershipCard({
            id: response.data.card.id,
            prefix: response.data.card.prefix,
            card_number: response.data.card.card_number,
            campaign_type: response.data.card.campaign_type,
            campaign_id: response.data.card.campaign.id,
            campaign_name: response.data.card.campaign.description,
            entity_name: response.data.card.payment_entity.name,
            amount: _amount,
            currency: items[0].amount.currency_code,
            creation_date: response.data.card.creation_date,
            payment_status: response.data.card.payment_status.id,
            payment_type: null,
          });

          _paypalOrder.push({
            reference_id: response.data.card.card_number,
            amount: {
              currency_code: items[0].amount.currency_code,
              value: _amount,
            },
            description: `Tesseramento PD - ${
              response.data.card.card_code || `${response.data.card.prefix}${response.data.card.card_number}`
            }`,
          });

          setOrderItems(_paypalOrder);
          setIsPaying(true);
          handleCloseCreateCardDialog();
          getUserCards();

          // Bundle GD+PD: crea la tessera GD figlia (importo 0, parent = tessera PD). R6/R17.
          if (renewMode === 'PD_GD' && campaignGd?.id && gdBundleRates.length > 0) {
            let wStatus = null;
            paymentStatus.forEach((s) => {
              if (s.type === 'W') wStatus = s.id;
            });
            API.post('/memcards', {
              user_id: userInfo.id,
              campaign_id: campaignGd.id,
              parent_id: response.data.card.id,
              rates: gdBundleRates,
              // La tessera GD deve pagare sull'entità master GD, non sull'entità PD.
              payment_entity_id: userInfo.entity_master_gd || gdEntitySel?.id,
              payment_online: 1,
              digital_format: Number(onlineMembershipCard),
              payment_status: wStatus,
            })
              .then(() => getUserCards())
              .catch((err) => HttpErrorHandler(err, setPopUpText));
          }
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    } else {
      showPopup('warning', "L'importo non può essere zero");
    }
  };

  /* -------------------------------- Bank Data ------------------------------- */
  const [beneficiary, setBeneficiary] = useState('');
  const [ibanCode, setIbanCode] = useState('');
  const [causal, setCausal] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [isBankDataLoading, setIsBankDataLoading] = useState(false);

  // Get Bank API Data
  const getBankData = () => {
    if (beneficiary.length === 0) {
      API.get('/paymentbank')
        .then((response) => {
          const data = response.data[0];
          setBeneficiary(data.intestatario);
          setIbanCode(data.iban);
          setCausal(data.causale);
          setPaymentNote(data.note);
          setIsBankDataLoading(false);
        })
        .catch((error) => {
          HttpErrorHandler(error, setPopUpText);
          showPopup(false);
        });
    }
  };

  const sendBankDataEmail = (e) => {
    e.preventDefault();

    API.post(`/paybanksendmail`, { id: membershipCard.id })
      .then((response) => {
        showPopup(true, response.data.message);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // operation after concluded payment
  const onPaymentDone = (paymentData) => {
    let payedStatusID;

    paymentStatus.forEach((staus) => {
      if (staus.type === 'P') {
        payedStatusID = staus.id;
      }
    });

    const data = {
      user_id: userInfo.id,
      campaign_id: membershipCard.campaign_id,
      payment_status: payedStatusID,
      payment_type: membershipCard.payment_type,
      payment_entity_id: membershipCard.entity_id,
      payment_online: 1,
    };

    if (paymentData) {
      const date = new Date();
      date.setHours(date.getHours() + 2);
      const newIsoDate = date.toISOString();
      data.payment_date = newIsoDate;
      data.payment_pp_trans_id = paymentData.id;
      data.payment_pp_acc_name = paymentData.name;
      data.payment_pp_acc_surname = paymentData.surname;
      data.payment_pp_acc_mail = paymentData.email;
      data.payment_pp_acc_status = paymentData.status;
      data.payment_pp_acc_id = paymentData.payer_id;
    }

    // In attesa di creazione richiesta
    API.put(`/memcards/${membershipCard.id}`, data)
      .then((response) => {
        getUserCards();
        handleCloseNewCard();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const handleUserPaying = (card) => {
    let _amount = 0.0;
    let _currency = '';
    const _memcard = {};
    const items = [];

    card.user_rates.forEach((rate) => {
      _currency = rate.currency;
      _amount += parseFloat(rate.price);
    });

    _memcard.id = card.id;
    _memcard.prefix = card.prefix;
    _memcard.card_number = card.card_number;
    _memcard.campaign_type = card.campaign_type || card.campaign?.campaign_type || null;
    _memcard.campaign_id = card.campaign.id;
    _memcard.campaign_name = card.campaign.description;
    _memcard.entity_name = card.payment_entity.name;
    _memcard.entity_id = card.payment_entity.id;
    _memcard.creation_date = card.creation_date;
    _memcard.payment_status = card.payment_status.id;
    _memcard.payment_type = card.peyment_type ? card.payment_type.id : null;
    _memcard.currency = _currency;
    _memcard.amount = _amount;

    items.push({
      reference_id: _memcard.card_number,
      amount: {
        currency_code: _memcard.currency,
        value: _memcard.amount,
      },
      description: `Tesseramento PD - ${_memcard.card_code || `${_memcard.prefix}${_memcard.card_number}`}`,
    });

    setOrderItems(items);
    setMembershipCard(_memcard);
    handleOpenNewCard(false);
    setIsPaying(true);
  };

  // Get initial user membership card price rates
  useEffect(() => {
    if (!isLoading) {
      if (positions.length > 0) {
        let userHighestPosition = {};
        userHighestPosition = positions.reduce((maxPosition, currentPosition) =>
          currentPosition.level > maxPosition.level ? currentPosition : maxPosition
        );
        setUserPosition(userHighestPosition);
      }

      const internalPos = positions.filter((position) => position.internal === 1);
      const externalPos = positions.filter((position) => position.internal === 0);

      setInternalPosition(internalPos);
      setExternalPosition(externalPos);

      if (positionTabValue === 0) {
        setSelectedPosition(externalPos);
      } else {
        setSelectedPosition(internalPos);
      }
    }
  }, [positions]);

  // Initial user data loading
  useEffect(() => {
    setIsLoading(true);
    getUserDetails();
  }, []);

  const [digitalCardApiCalled, setDigitalCardApiCalled] = useState(false);

  const createDigitalCardPdf = (card) => {
    API.get(`/users/digital_card_data`, {
      params: {
        user_id: userInfo.id,
        campaign_id: card.campaign_id,
      },
    }).then((response) => {
      setCardData({
        campaignPrefix: response.data.campaign_prefix,
        nameAlias: response.data.user_name_alias,
        name: response.data.user_name,
        surname: response.data.user_surname,
        secretary: 'Ufficio Adesioni',
        federation: response.data.user_federation,
        entity: response.data.user_entity,
        code: response.data.card_code,
        gdCard: response.data.is_gd_card,
      });

      setDigitalCardApiCalled(true);
    });
  };

  useEffect(() => {
    if (digitalCardApiCalled) {
      handleGeneratePDF();
    }
  }, [digitalCardApiCalled]);

  const [cardData, setCardData] = useState({
    campaignPrefix: '',
    nameAlias: '',
    name: '',
    surname: '',
    secretary: '',
    federation: '',
    entity: '',
    code: '',
    gdCard: false,
  });

  const handleGeneratePDF = () => {
    const element = document.getElementById('pdf-frame');

    // Formato pagina in base al tipo tessera: PD orizzontale, GD verticale (stesse dimensioni
    // del frame renderizzato in DigitalCard, così il PDF non taglia/deforma l'immagine).
    const { width: pageWidthPx, height: pageHeightPx, orientation } = getCardDimensions(cardData.gdCard);

    const pageWidthMm = pageWidthPx * 0.264583;
    const pageHeightMm = pageHeightPx * 0.264583;

    const options = {
      margin: 0,
      filename: 'digital_card.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: [pageWidthMm, pageHeightMm], orientation },
    };

    // html2pdf().set(options).from(element).save();

    html2pdf()
      .set(options)
      .from(element)
      .output('blob')
      .then((blob) => {
        const pdfUrl = URL.createObjectURL(blob);

        window.open(pdfUrl, '_blank');
      });

    setDigitalCardApiCalled(false);
  };

  // get user data
  useEffect(() => {
    let data;

    if (localStorage.getItem('userData') !== 'undefined') data = JSON.parse(localStorage.getItem('userData'));

    if (data) {
      const _currentProfile = data.roles.filter((role) => role.last_used);
      setCurrentProfile(_currentProfile);
    }
  }, []);

  if (currentProfile && currentProfile[0].prof_type_name !== 'USER') return <Navigate to={{ pathname: '/' }} />;

  return (
    <div>
      <Helmet>
        <title>Profilo Utente</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ height: '100%' }}>
        <Box width="100%" height="100%">
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              allowScrollButtonsMobile
              scrollButtons="auto"
              variant={is600Screen ? 'fullWidth' : 'scrollable'}
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
              sx={{ justifyContent: 'space-between' }}
            >
              <Tab label="Anagrafica" {...a11yProps(0, 'thriple')} icon={<AssignmentIcon />} iconPosition="start" />
              <Tab label="Tesseramento" {...a11yProps(1, 'thriple')} icon={<BadgeIcon />} iconPosition="start" />
              <Tab label="Cariche" {...a11yProps(2, 'thriple')} icon={<GppGoodIcon />} iconPosition="start" />
            </Tabs>
          </Box>

          {/* Anagrafiche Utente */}
          <MultipleTabPanel value={value} index={0}>
            {isLoading ? (
              LoadingUI()
            ) : errorMessage.length === 0 ? (
              <Grid container spacing={3} alignItems="flex-start !important">
                <Grid item xs={12} md={4}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      {/* User profile card */}
                      <Card sx={{ p: '24px' }}>
                        <List>
                          <ListItem sx={{ d: 'flex', justifyContent: 'center' }}>
                            <Avatar sx={{ width: '150px', height: '150px' }} src="/assets/images/img/user.png" />
                          </ListItem>

                          <ListItem sx={{ d: 'flex', justifyContent: 'center', pb: '20px' }}>
                            {userInfo.name_alias === null
                              ? `${userInfo.name} ${userInfo.surname}`
                              : userInfo.name_alias}
                          </ListItem>

                          <ListItem sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Stack direction="column" alignItems="center" justifyContent="center" spacing={1}>
                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                <Typography
                                  sx={{
                                    padding: '4px 10px',
                                    fontSize: '.8em',
                                    backgroundColor: userInfo.verified ? '#dbf6e5' : '#ffe4de',
                                    color: userInfo.verified ? '#118d57' : '#b71d18',
                                    borderRadius: '16px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                  }}
                                >
                                  {userInfo.verified ? 'Verificato' : 'Non Verificato'}
                                </Typography>

                                <Typography
                                  sx={{
                                    padding: '4px 10px',
                                    fontSize: '.8em',
                                    backgroundColor: userInfo.card_holder ? '#dbf6e5' : '#ffe4de',
                                    color: userInfo.card_holder ? '#118d57' : '#b71d18',
                                    borderRadius: '16px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                  }}
                                >
                                  {userInfo.card_holder ? 'Tesserato' : 'Non Tesserato'}
                                </Typography>
                              </Stack>

                              <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                                <Typography
                                  sx={{
                                    padding: '4px 10px',
                                    fontSize: '.8em',
                                    backgroundColor: userInfo.user_online ? '#dbf6e5' : '#ffe4de',
                                    color: userInfo.user_online ? '#118d57' : '#b71d18',
                                    borderRadius: '16px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                  }}
                                >
                                  {userInfo.user_online ? 'Online' : 'Offline'}
                                </Typography>

                                <Typography
                                  sx={{
                                    padding: '4px 10px',
                                    fontSize: '.8em',
                                    backgroundColor: !userInfo.blocked ? '#dbf6e5' : '#ffe4de',
                                    color: !userInfo.blocked ? '#118d57' : '#b71d18',
                                    borderRadius: '16px',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                  }}
                                >
                                  {!userInfo.blocked ? 'Attivo' : 'Bloccato'}
                                </Typography>
                              </Stack>
                            </Stack>
                          </ListItem>
                        </List>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: '24px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '16px',
                        }}
                      >
                        <CardHeader sx={{ p: '0' }} title="Gestione Comunicazioni" />
                        {/* Comunication preferences */}
                        <FormGroup sx={{ flexDirection: 'column' }}>
                          <FormControlLabel
                            control={<Switch checked={allowNews} onClick={onOpenNewsletterDialog} />}
                            label="Voglio ricevere comunicazioni"
                          />
                        </FormGroup>

                        <DecisionDialog
                          openDialog={openNewsletterDialog}
                          dialogTitle={`${allowNews ? 'Disattiva' : 'Attiva'} comunicazioni`}
                          dialogDescription={`Sei sicuro di voler ${
                            allowNews ? 'disattivare' : 'attivare'
                          } le comunicazioni?`}
                          closeOperation={onCloseNewsletterDialog}
                          requestOperation={changeNewsletterPermission}
                          requestText={allowNews ? 'Disattiva' : 'Attiva'}
                          closeText={'Chiudi'}
                          RequestButton={DefaultContainedButton}
                        />
                      </Card>
                    </Grid>

                    {/* Modify password section */}
                    <Grid item xs={12}>
                      <Card
                        sx={{
                          p: '24px',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '16px',
                        }}
                      >
                        <CardHeader sx={{ p: '0' }} title="Gestione Password" />
                        <DefaultContainedButton onClick={handleOpenResetPassword}>
                          Modifica Password
                        </DefaultContainedButton>
                      </Card>

                      <ResetPasswordDialog openDialog={openResetPassword} closeDialog={handleCloseResetPassword} />
                    </Grid>
                  </Grid>
                </Grid>

                {/* User information section */}
                <Grid item xs={12} md={8}>
                  <Card sx={{ p: '24px', minHeight: '764px' }}>
                    <form onSubmit={onUpdateUserData}>
                      {!isEditApisRunning ? (
                        <Grid container spacing={3} justifyContent="start !important">
                          {/* Peronal Title */}
                          <Grid item xs={12}>
                            <h4>Informazioni Personali</h4>
                          </Grid>

                          {/* Name */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField disabled defaultValue={name} label="Nome" />
                          </Grid>

                          {/* Surname */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField disabled defaultValue={surname} label="Cognome" />
                          </Grid>

                          {/* Alias */}
                          <Grid item xs={12} md={useAlias ? 6 : 12} sx={{ height: '80px' }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  disabled={!edit}
                                  checked={useAlias}
                                  onChange={(e) => {
                                    setUseAlias(e.target.checked);
                                  }}
                                />
                              }
                              label="Utilizza nome alias"
                            />
                          </Grid>
                          {useAlias && (
                            <>
                              {/* name alias */}
                              <Grid item xs={12} md={6}>
                                <CustomTextField
                                  error={errors.name_alias}
                                  disabled={!edit}
                                  onChange={(e) => {
                                    setAliasName(e.currentTarget.value);
                                  }}
                                  value={aliasName}
                                  label="Alias"
                                />
                              </Grid>
                            </>
                          )}

                          {/* gender */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              options={GenderList}
                              disabled={!edit}
                              getOptionLabel={(option) => option.gender}
                              isOptionEqualToValue={(option, value) => option.gender === value.gender}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.gender}>
                                  {option.gender}
                                </Box>
                              )}
                              onChange={(e, value) => {
                                setGender(value);
                              }}
                              defaultValue={gender}
                              renderInput={(params) => (
                                <TextField {...params} label="Sesso" error={errors.gender} variant="outlined" />
                              )}
                            />
                          </Grid>

                          {/* Birth Date */}
                          <Grid item xs={12} md={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                              <CustomDatePicker required disabled label="Data di Nascita" value={dayjs(birthDate)} />
                            </LocalizationProvider>
                          </Grid>

                          {/* Fiscal Code */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField disabled defaultValue={birthPlace} label="Luogo di Nascita" />
                          </Grid>

                          {/* Fiscal Code */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField disabled defaultValue={fiscalCode} label="Codice Fiscale" />
                          </Grid>

                          {/* Circolo */}
                          <Grid item xs={12} md={6} sx={{ alignSelf: 'start' }}>
                            <CustomTextField
                              disabled
                              defaultValue={userEntity ? userEntity.name : ''}
                              label="Circolo"
                            />
                          </Grid>

                          {/* Entità master GD — solo se l'utente è abilitato ai GD (età 16–29) */}
                          {birthDate && isGdEligibleAge(dayjs().diff(dayjs(birthDate), 'year')) && (
                            <Grid item xs={12} md={6} sx={{ alignSelf: 'start' }}>
                              <GdMasterEntitySelect
                                value={profileGdEntity}
                                onChange={setProfileGdEntity}
                                options={profileGdEntityList}
                                // Modificabile solo se non ancora impostata: una volta scelta è bloccata.
                                disabled={!edit || !!userInfo?.entity_master_gd}
                                label="Entità GD"
                              />
                            </Grid>
                          )}

                          {/* User phone number call code */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField disabled defaultValue={callCode} label="Prefisso" />
                          </Grid>

                          {/* Phone */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField
                              disabled
                              defaultValue={phoneNumber}
                              label="Cellulare"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {userInfo.verified_phone ? (
                                      <Tooltip arrow title="Numero verificato" placement="top">
                                        <VerifiedIcon sx={{ color: 'green !important' }} />
                                      </Tooltip>
                                    ) : (
                                      <Tooltip arrow title="Numero non verificato" placement="top">
                                        <WarningAmberIcon
                                          onClick={handleOpenVerifyPhoneDialog}
                                          sx={{ color: 'orange !important', cursor: 'pointer' }}
                                        />
                                      </Tooltip>
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />

                            {/* Phone number verification dialog */}
                            <Dialog fullWidth open={openVerifyPhoneDialog} onClose={() => {}}>
                              <DialogTitle>Verifica Numero</DialogTitle>

                              <DialogContent>
                                {/* Phone Verification */}
                                <Stack
                                  justifyContent="center"
                                  alignItems="center"
                                  gap="12px"
                                  id="register-email-token-container"
                                >
                                  <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                    {/* Phone Number */}
                                    <Stack
                                      direction={is600Screen ? 'row' : 'column'}
                                      justifyContent="center"
                                      alignItems="center"
                                      gap="6px"
                                    >
                                      <Typography variant="h6">Numero di telefono:</Typography>
                                      <Typography variant="span">
                                        {callCode}
                                        {phoneNumber}
                                      </Typography>
                                      {isUserVerifyingPhone && !verifiedPhone && (
                                        <Tooltip title="In attesa di verifica" placement="right">
                                          <AutorenewIcon sx={{ color: '#414670' }} />
                                        </Tooltip>
                                      )}

                                      {verifiedPhone && (
                                        <Tooltip title="Numero verificato" placement="right">
                                          <CheckIcon sx={{ color: 'green' }} />
                                        </Tooltip>
                                      )}
                                    </Stack>

                                    {isUserVerifyingPhone && !verifiedPhone && (
                                      <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                        {/* Input & Submit Button */}
                                        <Stack direction="row" justifyContent="center" alignItems="center" gap="12px">
                                          <CustomTextField
                                            size="small"
                                            inputProps={{ maxLength: 6 }}
                                            onChange={(e) => validatePhoneNumber(e, setPhoneVerificationCode)}
                                            label="Inserisci il codice"
                                          />
                                          <DefaultOutlinedButton
                                            disabled={verifiedPhone}
                                            onClick={() => onVerifyUserPhone()}
                                          >
                                            Verifica
                                          </DefaultOutlinedButton>
                                        </Stack>

                                        {/* Resend SMS */}
                                        <Box sx={{ textAlign: 'center' }}>
                                          <Typography>Non hai ricevuto l'sms?</Typography>
                                          <Typography
                                            sx={{ textDecoration: 'underline', color: 'gray', cursor: 'pointer' }}
                                            onClick={() => sendOtpSms()}
                                          >
                                            Invialo di nuovo
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    )}
                                    {!verifiedPhone && !isUserVerifyingPhone && (
                                      <DefaultOutlinedButton onClick={() => sendOtpSms()}>
                                        Verifica Numero
                                      </DefaultOutlinedButton>
                                    )}
                                  </Stack>
                                </Stack>
                              </DialogContent>
                              <DialogActions>
                                <DefaultOutlinedButton onClick={handleCloseVerifyPhoneDialog}>
                                  Chiudi
                                </DefaultOutlinedButton>
                              </DialogActions>
                            </Dialog>
                          </Grid>

                          {/* User Email Verification */}
                          <Grid item xs={12} md={6}>
                            <CustomTextField
                              disabled
                              defaultValue={email}
                              label="Email"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {userInfo.verified_email ? (
                                      <Tooltip arrow title="Email verificata" placement="top">
                                        <VerifiedIcon sx={{ color: 'green !important' }} />
                                      </Tooltip>
                                    ) : (
                                      <Tooltip arrow title="Email non verificata" placement="top">
                                        <WarningAmberIcon
                                          onClick={handleOpenVerifyEmailDialog}
                                          sx={{ color: 'orange !important', cursor: 'pointer' }}
                                        />
                                      </Tooltip>
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />

                            {/* Email verification dialog */}
                            <Dialog fullWidth open={openVerifyEmailDialog} onClose={() => {}}>
                              <DialogTitle>Verifica E-mail</DialogTitle>

                              <DialogContent>
                                <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                  {/* Email Address */}
                                  <Stack
                                    direction={is600Screen ? 'row' : 'column'}
                                    justifyContent="center"
                                    alignItems="center"
                                    gap="6px"
                                  >
                                    <Typography variant="h6">E-mail:</Typography>
                                    <Typography variant="span">{email}</Typography>
                                    {isUserVerifyingEmail && !verifiedEmail && (
                                      <Tooltip title="In attesa di verifica" placement="right">
                                        <AutorenewIcon sx={{ color: '#414670' }} />
                                      </Tooltip>
                                    )}
                                    {verifiedEmail && (
                                      <Tooltip title="Email verificata" placement="right">
                                        <CheckIcon sx={{ color: 'green' }} />
                                      </Tooltip>
                                    )}
                                  </Stack>
                                  {!verifiedEmail && isUserVerifyingEmail && (
                                    <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                      {/* Input & Submit Button */}
                                      <Stack direction="row" justifyContent="center" alignItems="center" gap="12px">
                                        <CustomTextField
                                          size="small"
                                          inputProps={{ maxLength: 6 }}
                                          onChange={(e) => validatePhoneNumber(e, setEmailVerificationCode)}
                                          type="tel"
                                          label="Inserisci il codice"
                                        />
                                        <DefaultOutlinedButton onClick={() => onVerifyUserEmail()}>
                                          Verifica
                                        </DefaultOutlinedButton>
                                      </Stack>

                                      {/* Resend Email verifcation code */}
                                      <Box sx={{ textAlign: 'center' }}>
                                        <Typography>Non hai ricevuto l'email?</Typography>
                                        <Typography
                                          sx={{ textDecoration: 'underline', color: 'gray', cursor: 'pointer' }}
                                          onClick={() => sendOtpEmail()}
                                        >
                                          Inviala di nuovo
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  )}

                                  {!verifiedEmail && !isUserVerifyingEmail && (
                                    <DefaultOutlinedButton onClick={() => sendOtpEmail()}>
                                      Verifica Email
                                    </DefaultOutlinedButton>
                                  )}
                                </Stack>
                              </DialogContent>

                              <DialogActions>
                                <DefaultOutlinedButton onClick={handleCloseVerifyEmailDialog}>
                                  Chiudi
                                </DefaultOutlinedButton>
                              </DialogActions>
                            </Dialog>
                          </Grid>

                          {/* User residence address section */}
                          <Grid item xs={12}>
                            <h4>Informazioni di Residenza</h4>
                          </Grid>

                          {/* Residence address Country */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              id="res-country"
                              disabled={!edit}
                              options={
                                resCountry?.name === italy.name
                                  ? resCountriesList.filter(
                                      (country) =>
                                        (!resRegion || country.id === resRegion.country_id) &&
                                        (!resProvince || country.id === resProvince.country_id)
                                    )
                                  : resCountriesList
                              }
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => option.name}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                  {option.name}
                                </Box>
                              )}
                              value={resCountry}
                              onChange={(e, value) => setResCountry(value)}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Paese *"
                                  error={errors.res_country_id}
                                  variant="outlined"
                                />
                              )}
                            />
                          </Grid>

                          {/* Residence address Region */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              id="res-region"
                              freeSolo={resCountry?.name !== italy?.name}
                              disabled={!edit}
                              options={resRegionList.filter(
                                (region) =>
                                  (!resCountry || region.country_id === resCountry.id) &&
                                  (!resProvince || region.id === resProvince.region_id)
                              )}
                              isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') {
                                  return option;
                                }
                                if (option.inputValue) {
                                  return option.inputValue;
                                }
                                return option.region_name;
                              }}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.region_name}>
                                  {option.region_name}
                                </Box>
                              )}
                              value={
                                resRegionName === undefined ||
                                resRegionName === null ||
                                resCountry?.name === italy?.name
                                  ? resRegion
                                  : resRegionName
                              }
                              onChange={(e, value) => {
                                if (typeof value === 'string') {
                                  setResRegion(value);
                                } else if (value && value.inputValue) {
                                  setResRegion(value.inputValue);
                                } else {
                                  setResRegion(value);
                                }
                              }}
                              onInputChange={(e, value) => {
                                if (resCountry?.name !== italy?.name) {
                                  setResRegionName(value);
                                } else {
                                  setResRegionName(null);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Regione *"
                                  error={errors.res_region_name}
                                  variant="outlined"
                                />
                              )}
                            />
                          </Grid>

                          {/* Residence address Province */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              id="res-province"
                              freeSolo={resCountry?.name !== italy?.name}
                              disabled={!edit || (resCountry && resCountry?.name !== italy.name)}
                              options={resProvincesList.filter(
                                (province) =>
                                  (!resRegion || province.region_name === resRegion.region_name) &&
                                  (!resCity || province.id === resCity.province_id) &&
                                  (!resCountry || province.country_id === resCountry.id)
                              )}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') {
                                  return option;
                                }
                                if (option.inputValue) {
                                  return option.inputValue;
                                }
                                return option.name;
                              }}
                              value={resProvince}
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                  {option.name}
                                </Box>
                              )}
                              onChange={(e, value) => {
                                if (typeof value === 'string') {
                                  setResProvince(value);
                                } else if (value && value.inputValue) {
                                  setResProvince(value.inputValue);
                                } else {
                                  setResProvince(value);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={`Provincia ${resCountry?.name === italy.name ? '*' : ''}`}
                                  variant="outlined"
                                  error={errors.res_province_id}
                                />
                              )}
                            />
                          </Grid>

                          {/* Residence address City */}
                          <Grid item xs={12} md={6}>
                            <CustomAutocomplete
                              id="res-city"
                              freeSolo={resCountry?.name !== italy?.name}
                              disabled={!edit}
                              options={resCitiesList.filter(
                                (city) => !resProvince || city.province_id === resProvince.id
                              )}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') {
                                  return option;
                                }
                                if (option.inputValue) {
                                  return option.inputValue;
                                }
                                return option.name;
                              }}
                              value={
                                resCityName === undefined || resCityName === null || resCountry?.name === italy?.name
                                  ? resCity
                                  : resCityName
                              }
                              renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.id}>
                                  {option.name}
                                </Box>
                              )}
                              onChange={(e, value) => {
                                if (typeof value === 'string') {
                                  setResCity(value);
                                } else if (value && value.inputValue) {
                                  setResCity(value.inputValue);
                                } else {
                                  setResCity(value);
                                }
                              }}
                              onInputChange={(e, value) => {
                                if (resCountry?.name !== italy?.name) {
                                  setResCityName(value);
                                } else {
                                  setResCityName(null);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Comune *"
                                  variant="outlined"
                                  error={errors.res_city_id || errors.res_city_name}
                                />
                              )}
                            />
                          </Grid>

                          {/* Residence address Street */}
                          <Grid item xs={12} sm={6}>
                            <CustomTextField
                              id="res-street"
                              disabled={!edit}
                              error={errors.res_street}
                              fullWidth
                              name="resStreet"
                              label="Indirizzo di Residenza *"
                              value={resStreet}
                              onChange={(e) => setResStreet(e.target.value)}
                              inputProps={{
                                maxLength: 50,
                              }}
                            />
                          </Grid>

                          {/* Residence address postal code */}
                          <Grid item xs={12} sm={6}>
                            <CustomTextField
                              id="res-zipcode"
                              disabled={!edit}
                              error={errors.res_zip_code}
                              fullWidth
                              label="CAP di Residenza *"
                              name="resZipCode"
                              onChange={(e) => setResZipCode(e.target.value)}
                              value={resZipCode}
                              inputProps={{
                                maxLength: 15,
                              }}
                            />
                          </Grid>

                          {/* User permenant address */}
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Switch
                                  color="primary"
                                  checked={resEqualHome}
                                  onChange={onChangeHomePreferences}
                                  disabled={!edit}
                                />
                              }
                              label="Le informazioni di Domicilio corrispondono a quelle di Residenza"
                            />
                          </Grid>

                          {!resEqualHome && (
                            <Grid container spacing={3} sx={{ margin: '0' }}>
                              {/* Permenant address Title */}
                              <Grid item xs={12}>
                                <h4>Informazioni di Domicilio</h4>
                              </Grid>

                              {/* Permenant address Country */}
                              <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                  id="home-country"
                                  disabled={!edit}
                                  options={
                                    homeCountry?.name === italy.name
                                      ? homeCountriesList.filter(
                                          (country) =>
                                            (!homeRegion || country.id === homeRegion.country_id) &&
                                            (!homeProvince || country.id === homeProvince.country_id)
                                        )
                                      : homeCountriesList
                                  }
                                  getOptionLabel={(option) => option.name || ''}
                                  isOptionEqualToValue={(option, value) => value.id === option.id}
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                      {option.name}
                                    </Box>
                                  )}
                                  value={homeCountry}
                                  onChange={(e, value) => setHomeCountry(value)}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Paese *"
                                      error={errors.home_country_id}
                                      variant="outlined"
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Permenant address Region */}
                              <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                  id="home-region"
                                  freeSolo={homeCountry?.name !== italy?.name}
                                  disabled={!edit}
                                  options={homeRegionList.filter(
                                    (region) =>
                                      (!homeCountry || region.country_id === homeCountry.id) &&
                                      (!homeProvince || region.region_name === homeProvince.region_name)
                                  )}
                                  isOptionEqualToValue={(option, value) => value.region_name === option.region_name}
                                  getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                      return option;
                                    }
                                    if (option.inputValue) {
                                      return option.inputValue;
                                    }
                                    return option.region_name || '';
                                  }}
                                  value={
                                    homeRegionName !== null &&
                                    homeRegionName !== undefined &&
                                    homeCountry?.name !== italy?.name
                                      ? homeRegionName
                                      : homeRegion
                                  }
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.region_name}>
                                      {option.region_name}
                                    </Box>
                                  )}
                                  onChange={(e, value) => {
                                    if (typeof value === 'string') {
                                      setHomeRegion(value);
                                    } else if (value && value.inputValue) {
                                      setHomeRegion(value.inputValue);
                                    } else {
                                      setHomeRegion(value);
                                    }
                                  }}
                                  onInputChange={(e, value) => {
                                    if (homeCountry?.name !== italy?.name) {
                                      setHomeRegionName(value);
                                    } else {
                                      setHomeRegionName(null);
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Regione *"
                                      variant="outlined"
                                      error={errors.home_region_name}
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Permenant address Province */}
                              <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                  id="home-province"
                                  freeSolo={homeCountry?.name !== italy?.name}
                                  disabled={!edit || (homeCountry && homeCountry?.name !== italy.name)}
                                  options={homeProvincesList.filter(
                                    (province) =>
                                      (!homeRegion || province.region_name === homeRegion.region_name) &&
                                      (!homeCity || province.id === homeCity.province_id)
                                  )}
                                  isOptionEqualToValue={(option, value) => value.id === option.id}
                                  getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                      return option;
                                    }
                                    if (option.inputValue) {
                                      return option.inputValue;
                                    }
                                    return option.name || '';
                                  }}
                                  value={homeProvince}
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                      {option.name}
                                    </Box>
                                  )}
                                  onChange={(e, value) => {
                                    if (typeof value === 'string') {
                                      setHomeProvince(value);
                                    } else if (value && value.inputValue) {
                                      setHomeProvince(value.inputValue);
                                    } else {
                                      setHomeProvince(value);
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label={`Provincia ${homeCountry?.name === italy.name ? '*' : ''}`}
                                      variant="outlined"
                                      error={errors.home_province_id}
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Permenant address City */}
                              <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                  id="home-city"
                                  freeSolo={homeCountry?.name !== italy?.name}
                                  disabled={!edit}
                                  options={homeCitiesList.filter(
                                    (city) => !homeProvince || city.province_id === homeProvince.id
                                  )}
                                  isOptionEqualToValue={(option, value) => value.id === option.id}
                                  getOptionLabel={(option) => {
                                    if (typeof option === 'string') {
                                      return option;
                                    }
                                    if (option.inputValue) {
                                      return option.inputValue;
                                    }
                                    return option.name || '';
                                  }}
                                  value={
                                    homeCityName !== null &&
                                    homeCityName !== undefined &&
                                    homeCountry?.name !== italy?.name
                                      ? homeCityName
                                      : homeCity
                                  }
                                  renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                      {option.name}
                                    </Box>
                                  )}
                                  onChange={(e, value) => {
                                    if (typeof value === 'string') {
                                      setHomeCity(value);
                                    } else if (value && value.inputValue) {
                                      setHomeCity(value.inputValue);
                                    } else {
                                      setHomeCity(value);
                                    }
                                  }}
                                  onInputChange={(e, value) => {
                                    if (homeCountry?.name !== italy?.name) {
                                      setHomeCityName(value);
                                    } else {
                                      setHomeCityName(null);
                                    }
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Comune *"
                                      variant="outlined"
                                      error={errors.home_city_id || errors.home_city_name}
                                    />
                                  )}
                                />
                              </Grid>

                              {/* Permenant address Street */}
                              <Grid item xs={12} sm={6}>
                                <CustomTextField
                                  id="home-street"
                                  error={errors.home_street}
                                  disabled={!edit}
                                  fullWidth
                                  label="Indirizzo di Domicilio *"
                                  onChange={(e) => setHomeStreet(e.target.value)}
                                  defaultValue={homeStreet}
                                  inputProps={{
                                    maxLength: 50,
                                  }}
                                />
                              </Grid>

                              {/* Permenant address postal code */}
                              <Grid item xs={12} sm={6}>
                                <CustomTextField
                                  id="home-zipcode"
                                  disabled={!edit}
                                  error={errors.home_zip_code}
                                  fullWidth
                                  label="CAP di Domicilio *"
                                  onChange={(e) => setHomeZipCode(e.currentTarget.value)}
                                  defaultValue={homeZipCode}
                                  inputProps={{
                                    maxLength: 15,
                                  }}
                                />
                              </Grid>
                            </Grid>
                          )}

                          {/* User details footer section */}
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                              {edit && (
                                <DefaultOutlinedButton onClick={handleCloseEditButtonClick}>
                                  Annulla
                                </DefaultOutlinedButton>
                              )}

                              <DefaultContainedButton type="submit">
                                {edit ? 'Salva' : 'Modifica'}
                              </DefaultContainedButton>
                            </Box>
                          </Grid>
                        </Grid>
                      ) : (
                        <Stack
                          direction="row"
                          justifyContent="center"
                          alignItems="center"
                          sx={{ minHeight: resEqualHome ? '764px' : '1052px' }}
                        >
                          <CircularProgress />
                        </Stack>
                      )}
                    </form>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              ErrorUI(errorMessage)
            )}
          </MultipleTabPanel>

          {/* User membership card section */}
          <MultipleTabPanel value={value} index={1}>
            {isLoading || isCardSectionLoading ? (
              LoadingUI()
            ) : errorMessage.length === 0 ? (
              <>
                <Stack direction="row" justifyContent="end" gap="12px">
                  {/* Renderizzata fuori schermo (NON display:none) così l'auto-fit di
                      circolo/federazione può misurare la larghezza del testo prima del PDF. */}
                  <div style={{ position: 'absolute', left: '-99999px', top: 0, pointerEvents: 'none' }}>
                    <DigitalCard {...cardData} />
                  </div>

                  <DefaultContainedButton
                    onClick={openRenewDialog}
                    disabled={isBlacklistedEntity}
                    startIcon={<AddIcon />}
                  >
                    Crea Tessera
                  </DefaultContainedButton>
                </Stack>

                <RenewCardChoiceDialog
                  open={renewDialogOpen}
                  onClose={() => setRenewDialogOpen(false)}
                  options={renewOptions}
                  onChoose={handleRenewChoose}
                  loading={renewLoading}
                  needGdEntity={needGdEntity}
                  gdEntityValue={gdEntitySel}
                  gdEntityOptions={gdEntityList}
                  onGdEntityChange={setGdEntitySel}
                  gdFree={!!gdParentCardId}
                />
                {/* Checkout dialog for payment */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ padding: '0 0 24px' }}>
                  <Dialog fullWidth open={openNewCard} onClose={() => {}}>
                    <CardHeader
                      sx={{ paddingTop: '0' }}
                      avatar={
                        <IconButton
                          edge="start"
                          color="inherit"
                          sx={{ display: paymentMethod.select ? 'flex' : 'none' }}
                          onClick={() => setPaymentMethod({ select: false, method: null, type: null })}
                          aria-label="back"
                        >
                          <ArrowBackIosNewIcon />
                        </IconButton>
                      }
                    />

                    {subscriptionApiCalled && subscriptionApiError.length === 0 ? (
                      <form>
                        <DialogContent>
                          {!isPaying && !paypalSuccess && !paymentMethod.select ? (
                            <Grid container spacing={3}>
                              <Grid item xs={12} sx={{ overflow: 'auto' }}>
                                {userRates.length > 0 ? (
                                  <>
                                    {activeCampaign?.description && (
                                      <DialogTitle>{`Tesseramento ${activeCampaign.description}`}</DialogTitle>
                                    )}
                                    <Alert severity="error" variant="filled" sx={{ marginBottom: '8px' }}>
                                      Se ricopri incarichi istituzionali (consigliere/a, assessore/a, sindaco/a,
                                      presidente, parlamentare) inserisci la tua carica nella relativa sezione
                                      (“cariche”) prima di procedere al tesseramento.
                                    </Alert>
                                    <RatesTable
                                      rates={userRates}
                                      disabledStates={disabledStates}
                                      otherValue={otherValue}
                                      setOtherValue={setOtherValue}
                                      onRateChange={handleRateChange}
                                      onToggleEditable={handleDisabledInput}
                                      getReadonlyPrice={(rate) => (isGdAvailable ? rate.price_discounted : rate.price)}
                                      totalPrice={totalPrice}
                                    />
                                    <FormControlLabel
                                      sx={{ paddingTop: '16px' }}
                                      control={
                                        <Switch
                                          checked={onlineMembershipCard}
                                          onChange={() => setOnlineMembershipCard(!onlineMembershipCard)}
                                        />
                                      }
                                      label={onlineMembershipCard ? 'Tessera Online' : 'Tessera Cartacea'}
                                    />
                                  </>
                                ) : (
                                  <Typography color="error">Nessuna tariffa disponibile</Typography>
                                )}
                              </Grid>
                            </Grid>
                          ) : isPaying && !paymentMethod.select ? (
                            // payment method selection card
                            <Grid container rowGap={2}>
                              {filterPaypalOnly(paymentTypes, isGdPayment).map((type) => (
                                <Grid key={type.id} item xs={12}>
                                  <PaymentMethodOption
                                    type={type}
                                    onSelect={(selected) => {
                                      membershipCard.payment_type = selected.id;
                                      setPaymentMethod({ select: true, method: selected.name, type: selected.type });
                                    }}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          ) : isPaying && paymentMethod.select && paymentMethod.type === 'PCC' ? (
                            // paypal chackout option
                            <PaypalCheckout
                              purchaseItems={orderItems}
                              setPaypalSuccess={setPaypalSuccess}
                              setIsPaying={setIsPaying}
                              paymentDone={onPaymentDone}
                            />
                          ) : isPaying && paymentMethod.select && paymentMethod.type === 'BB' ? (
                            // Bank transfer payment option

                            isBankDataLoading ? (
                              LoadingUI()
                            ) : (
                              <Grid container>
                                <BankTransferInfo
                                  beneficiary={beneficiary}
                                  iban={ibanCode}
                                  causale={
                                    membershipCard.card_code || `${membershipCard.prefix}${membershipCard.card_number}`
                                  }
                                  amount={getPrice(membershipCard.amount)}
                                  currency={membershipCard.currency}
                                  note={paymentNote}
                                />
                              </Grid>
                            )
                          ) : (
                            // payment complete message
                            <Stack
                              direction="column"
                              justifyContent="center"
                              alignItems="center"
                              gap="25px"
                              sx={{ paddingY: 4, textAlign: 'center' }}
                            >
                              <CheckCircleOutlineIcon sx={{ fontSize: '5em', color: 'green' }} />
                              <Typography variant="h3">Pagamento andato buon fine!</Typography>
                            </Stack>
                          )}
                        </DialogContent>

                        <DialogActions
                          sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: '8px' }}
                        >
                          {paymentMethod.type === 'BB' && (
                            <Tooltip title="Copia dati" arrow placement="top">
                              <Box>
                                <CustomIconButton
                                  sx={{ height: '40px !important' }}
                                  onClick={() => {
                                    const copyText = navigator.clipboard.writeText(
                                      `Beneficiario: ${beneficiary}\nIBAN: ${ibanCode}\nCausale: Tesseramento PD - ${
                                        membershipCard.card_code ||
                                        `${membershipCard.prefix}${membershipCard.card_number}`
                                      }\nImporto: ${getPrice(membershipCard.amount)} ${membershipCard.currency}`
                                    );
                                    showPopup('info', 'Copiato con successo');

                                    return copyText;
                                  }}
                                >
                                  <ContentCopy sx={{ height: '24px !important' }} />
                                </CustomIconButton>
                              </Box>
                            </Tooltip>
                          )}

                          {paymentMethod.type === 'BB' && (
                            <Tooltip title="Invia dati via mail" arrow placement="top">
                              <Box sx={{ marginLeft: '0 !important' }}>
                                <CustomIconButton onClick={sendBankDataEmail}>
                                  <EmailOutlinedIcon />
                                </CustomIconButton>
                              </Box>
                            </Tooltip>
                          )}

                          <DefaultOutlinedButton
                            onClick={() => {
                              return handleCloseNewCard();
                            }}
                          >
                            Chiudi
                          </DefaultOutlinedButton>
                          <DefaultContainedButton
                            customStyles={{ display: !isPaying && !paypalSuccess ? 'block' : 'none' }}
                            onClick={handleOpenCreateCardDialog}
                            disabled={!isSaveEnabled}
                          >
                            Crea
                          </DefaultContainedButton>
                        </DialogActions>
                      </form>
                    ) : subscriptionApiError?.length > 0 ? (
                      ErrorUI(subscriptionApiError)
                    ) : (
                      LoadingUI()
                    )}

                    {!subscriptionApiCalled && (
                      <DialogActions>
                        <DefaultOutlinedButton onClick={handleCloseNewCard}>Chiudi</DefaultOutlinedButton>
                      </DialogActions>
                    )}
                  </Dialog>
                  <Dialog open={openCreateCardDialog} onClose={handleCloseCreateCardDialog}>
                    <DialogTitle>Sei sicuro di voler procedere al tesseramento?</DialogTitle>
                    <DialogContent>
                      <Typography>
                        Proseguendo la tariffa libera non sarà più modificabile e il contributo del tesseramento sarà
                        di:
                        {` ${totalPrice.toFixed(2)} €`}
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <DefaultContainedButton onClick={onCreateNewMembershipCard}>Continua</DefaultContainedButton>
                      <DefaultOutlinedButton onClick={handleCloseCreateCardDialog}>Chiudi</DefaultOutlinedButton>
                    </DialogActions>
                  </Dialog>
                </Stack>
                {isBlacklistedEntity ? (
                  <Alert variant="filled" severity="error" sx={{ marginTop: '24px' }}>
                    Al momento il tesseramento è stato disabilitato.
                  </Alert>
                ) : (
                  <>
                    <Alert variant="filled" severity="error">
                      Se ricopri incarichi istituzionali (consigliere/a, assessore/a, sindaco/a, presidente,
                      parlamentare) inserisci la tua carica nella relativa sezione (“cariche”) prima di procedere al
                      tesseramento.{' '}
                    </Alert>
                    {!!hasDownloadableCard && !!userInfo.verified && (
                      <Alert severity="warning" sx={{ marginBottom: '8px', marginTop: '8px' }}>
                        Devi consentire i popup del browser per scaricare la tessera.
                      </Alert>
                    )}
                  </>
                )}
                <Card sx={{ padding: '24px', marginTop: '10px' }}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell />
                          <TableCell>Campagna</TableCell>
                          <TableCell>Stato Tessera</TableCell>
                          <TableCell>Entità</TableCell>
                          <TableCell>Data Tesseramento</TableCell>
                          <TableCell>Numero Tessera</TableCell>
                          <TableCell>Metodo di Pagamento</TableCell>
                          <TableCell>Quota Tessera</TableCell>
                          <TableCell>Stato Pagamento</TableCell>
                        </TableRow>
                      </TableHead>
                      {cards.length > 0 ? (
                        <TableBody>
                          {cards.map((card) => (
                            <TableRow key={card.id}>
                              <TableCell>
                                {Number(card.prefix) >= 2024 && (
                                  <Tooltip
                                    arrow
                                    title={
                                      card.payment_status?.type === 'P' && userInfo.verified
                                        ? 'Scarica tessera digitale'
                                        : 'Per scaricare la tessera digitale devi avere un account verificato e la tessera pagata'
                                    }
                                    placement="bottom"
                                  >
                                    <Box>
                                      <CustomIconButton
                                        sx={{
                                          color: '#212b36',
                                        }}
                                        disabled={card.payment_status?.type !== 'P' || !userInfo.verified}
                                        onClick={() => createDigitalCardPdf(card)}
                                      >
                                        <CreditCardIcon />
                                      </CustomIconButton>
                                    </Box>
                                  </Tooltip>
                                )}

                                {!card.parent_id &&
                                  card.payment_status?.type !== 'P' &&
                                  card.payment_type?.type !== 'CS' && (
                                    <Tooltip arrow placement="bottom" title="Procedi al pagamento">
                                      <Box>
                                        <CustomIconButton
                                          sx={{
                                            color: '#212b36',
                                          }}
                                          onClick={() => handleUserPaying(card)}
                                        >
                                          <ShoppingCart />
                                        </CustomIconButton>
                                      </Box>
                                    </Tooltip>
                                  )}
                              </TableCell>
                              <TableCell>{card.campaign.description}</TableCell>
                              <TableCell>
                                <CommonLabel
                                  textColor={dayjs(card.campaign.end_date) > dayjs(new Date()) ? '#007867' : '#b71d18'}
                                  bgColor={
                                    dayjs(card.campaign.end_date) > dayjs(new Date()) ? '#22c55e29' : '#ff563029'
                                  }
                                  text={dayjs(card.campaign.end_date) > dayjs(new Date()) ? 'Attiva' : 'Scaduta'}
                                />
                              </TableCell>
                              <TableCell>
                                {typeof card.payment_entity === 'string'
                                  ? card.payment_entity
                                  : card.payment_entity?.name || '-'}
                              </TableCell>
                              <TableCell>{dayjs(card.created_at).format('DD/MM/YYYY')}</TableCell>
                              <TableCell>
                                {card.card_code || `${card.campaign_type || ''}${card.prefix}${card.card_number}`}
                              </TableCell>
                              <TableCell>{card.payment_type ? card.payment_type.name : '//'}</TableCell>
                              <TableCell>{`${card.price?.toFixed(2)}€`}</TableCell>
                              <TableCell>
                                <CommonLabel
                                  textColor={card.payment_status.type === 'P' ? '#007867' : '#b76e00'}
                                  bgColor={card.payment_status.type === 'P' ? '#22c55e29' : '#ffab0029'}
                                  text={card.payment_status.name}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      ) : (
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={9} align="center">
                              <Typography variant="body2">Nessuna tessera disponibile</Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                </Card>
              </>
            ) : (
              ErrorUI(errorMessage)
            )}
          </MultipleTabPanel>

          {/* User position tab */}
          <MultipleTabPanel value={value} index={2}>
            {isLoading ? (
              LoadingUI()
            ) : errorMessage.length === 0 ? (
              <>
                {positionTabValue === 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'end' }}>
                    <DefaultContainedButton onClick={handleOpenNewPosition} startIcon={<AddIcon />}>
                      Aggiungi Carica
                    </DefaultContainedButton>

                    <NewPositionDialog
                      openDialog={openNewPosition}
                      closeDialog={handleCloseNewPosition}
                      userAuth={'user'}
                      userInfo={userInfo}
                      refreshFunction={handleNewPosition}
                    />
                  </Box>
                )}

                <Card sx={{ marginTop: '24px' }}>
                  {/* Filter Tabs */}
                  <Tabs
                    variant="fullWidth"
                    value={positionTabValue}
                    onChange={handleChangePositionTab}
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    textColor="secondary"
                    indicatorColor="secondary"
                    sx={{ boxShadow: 'rgba(145, 158, 171, 0.08) 0px -2px 0px 0px inset', padding: '0px 24px 0' }}
                  >
                    <Tab
                      disableRipple
                      icon={
                        <Typography
                          sx={{
                            backgroundColor: positionTabValue === 0 ? '#000000' : '#edeff1',
                            borderRadius: '6px',
                            color: positionTabValue === 0 ? '#ffffff' : '#637381',
                            padding: '2px 8px',
                            fontSize: '1em',
                            fontWeight: 'bold',
                          }}
                        >
                          {externalPosition.length}
                        </Typography>
                      }
                      iconPosition="end"
                      label="Cariche Istituzionali"
                    />
                    <Tab
                      disableRipple
                      icon={
                        <Typography
                          sx={{
                            backgroundColor: positionTabValue === 1 ? '#22c55e' : '#dbf6e5',
                            borderRadius: '6px',
                            color: positionTabValue === 1 ? '#ffffff' : '#118d57',
                            padding: '2px 8px',
                            fontSize: '1em',
                            fontWeight: 'bold',
                          }}
                        >
                          {internalPosition.length}
                        </Typography>
                      }
                      iconPosition="end"
                      label="Cariche Politiche"
                    />
                  </Tabs>

                  <TableContainer sx={{ padding: '24px' }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Stato Carica</TableCell>
                          <TableCell>Carica</TableCell>
                          {positionTabValue === 0 && <TableCell>Email Istituzionale</TableCell>}
                          {positionTabValue === 1 && <TableCell>Entità</TableCell>}
                          <TableCell>Data Inizio</TableCell>
                          <TableCell>Data Fine</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      {selectedPosition.length > 0 ? (
                        <TableBody>
                          <EditPositionDialog
                            openDialog={openEditPosition}
                            closeDialog={handleCloseEditPosition}
                            positionToEdit={positionToEdit}
                            setPositionToEdit={setPositionToEdit}
                            userAuth={'user'}
                            userInfo={userInfo}
                            refreshFunction={() => handleNewPosition()}
                          />

                          {/* Delete Position Dialog */}
                          <DecisionDialog
                            openDialog={openDeletePosition}
                            dialogTitle={'Elimina carica'}
                            dialogDescription={`Sei sicuro di voler eliminare la carica: ${positionToEdit.name} ?`}
                            closeOperation={closeDeletePositionDialog}
                            requestOperation={deletePosition}
                            requestText={'Elimina'}
                            closeText={'Chiudi'}
                            RequestButton={RedOutlinedButton}
                          />

                          {selectedPosition.map((position) => (
                            <TableRow key={position.id}>
                              <TableCell>{PositionStatusLabel(position, 'label')}</TableCell>
                              <TableCell>{position.name}</TableCell>
                              {position.internal === 0 && (
                                <TableCell>{position.email ? position.email : '/'}</TableCell>
                              )}

                              {position.internal === 1 && <TableCell>{position.entity_name}</TableCell>}
                              <TableCell>
                                {position.start_date ? dayjs(position.start_date).format('DD/MM/YYYY') : '/'}
                              </TableCell>
                              <TableCell>
                                {position.end_date ? dayjs(position.end_date).format('DD/MM/YYYY') : '/'}
                              </TableCell>
                              <TableCell>
                                <Stack
                                  direction="row"
                                  justifyContent="center"
                                  alignItems="center"
                                  gap="12px"
                                  maxWidth="150px"
                                >
                                  <Tooltip title="Modifica Carica" arrow placement="top">
                                    <Box>
                                      <CustomIconButton onClick={() => handleOpenEditPosition(position)}>
                                        <EditIcon />
                                      </CustomIconButton>
                                    </Box>
                                  </Tooltip>
                                  {position.internal === 0 && (
                                    <Tooltip title="Elimina Carica" arrow placement="top">
                                      <Box>
                                        <CustomIconButton onClick={() => openDeletePositionDialog(position)}>
                                          <DeleteIcon />
                                        </CustomIconButton>
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      ) : (
                        <TableBody>
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              Nessuna carica disponibile
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                </Card>
              </>
            ) : (
              ErrorUI(errorMessage)
            )}
          </MultipleTabPanel>
        </Box>
      </Container>
    </div>
  );
}
